/* eslint-disable max-len */
import React from 'react'
import PropTypes from 'prop-types'
import Modal from 'react-modal'
import cx from 'classnames'

import styles from './modal-box.module.css'

const noop = Function.prototype

const propTypes = {
    aria: PropTypes.shape({
        describedby: PropTypes.string,
        labelledby: PropTypes.string
    }),
    ariaHideApp: PropTypes.bool,
    blindClassName: PropTypes.string,
    bodyOpenClassName: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    closeTimeoutMS: PropTypes.number,
    contentLabel: PropTypes.string,
    contentRef: PropTypes.func,
    htmlOpenClassName: PropTypes.string,
    isOpen: PropTypes.bool,
    onAfterOpen: PropTypes.func,
    onRequestClose: PropTypes.func,
    overlayClassName: PropTypes.string,
    overlayRef: PropTypes.func,
    parentSelector: PropTypes.func,
    portalClassName: PropTypes.string,
    role: PropTypes.string,
    shouldCloseOnEsc: PropTypes.bool,
    shouldCloseOnOverlayClick: PropTypes.bool,
    shouldFocusAfterRender: PropTypes.bool,
    shouldReturnFocusAfterClose: PropTypes.bool,
    style: PropTypes.shape({
        // eslint-disable-next-line react/forbid-prop-types
        content: PropTypes.objectOf(PropTypes.any),
        // eslint-disable-next-line react/forbid-prop-types
        overlay: PropTypes.objectOf(PropTypes.any)
    })
}

const defaultProps = {
    ariaHideApp: false,
    closeTimeoutMS: 0,
    contentLabel: '',
    isOpen: false,
    onAfterOpen: noop,
    onRequestClose: noop,
    role: 'dialog',
    shouldCloseOnEsc: false,
    shouldCloseOnOverlayClick: false,
    shouldFocusAfterRender: true,
    shouldReturnFocusAfterClose: true
}

/**
 * A simple wrapper around the react-modal modal component.
 *
 * @see {@link https://github.com/reactjs/react-modal}
 *
 * @example <caption>Basic usage</caption>
 * <code>
 *     <ModalBox isOpen={true}>
 *        My custom modal content
 *     </ModalBox>
 * </code>
 *
 * @param {Object} props - The current component properties
 * @param {Object} [props.aria] - Additional aria attributes (optional)
 * @param {boolean} [props.ariaHideApp=false] - Indicating if the appElement should be hidden
 * @param {string} [props.blindClassName] - DEPRECATED: Use overlayClassName instead
 * @param {string} [props.bodyOpenClassName] - String className to be applied to the document.body
 * @param {ReactElement} [props.children] - The component childs
 * @param {string} [props.className] - Merging optional classname for wrapper
 * @param {number} [props.closeTimeoutMS=0] - Number indicating the milliseconds to wait before closing the modal
 * @param {string} [props.contentLabel=''] - The aria-label for the wrapper
 * @param {Function} [props.contentRef] - Content ref callback
 * @param {string} [props.htmlOpenClassName] - String className to be applied to the document.html
 * @param {booelan} [props.isOpen=false] - Whether the modal is visible or not
 * @param {Function} [props.onAfterOpen=noop] - Callback after opening
 * @param {Function} [props.onRequestClose=noop] - Callback after user closes modal
 * @param {string} [props.overlayClassName] - String className to be applied to the overlay
 * @param {Function} [props.overlayRef] - Overlay ref callback
 * @param {Function} [props.parentSelector] - Will be called to get the parent element that the modal will be attached to.
 * @param {string} [props.portalClassName] - String className to be applied to the portal
 * @param {string} [props.role='dialog'] - The aria role for the dialog wrapper
 * @param {boolean} [props.shouldCloseOnEsc=false] - Indicating if pressing the esc key should close the modal
 * @param {boolean} [props.shouldCloseOnOverlayClick=false] - Indicating if the overlay should close the modal
 * @param {boolean} [props.shouldFocusAfterRender=true] - Indicating if the modal should be focused after render
 * @param {boolean} [props.shouldReturnFocusAfterClose=true] - If the modal should restore focus to the element that had focus prior to its display
 * @param {Object} [props.style] - Object indicating styles to be used for the modal
 * @returns {ReactElement} The modal markup
 */
function ModalBox(props) {
    const {
        aria,
        ariaHideApp,
        blindClassName,
        bodyOpenClassName,
        children,
        className,
        closeTimeoutMS,
        contentLabel,
        contentRef,
        htmlOpenClassName,
        isOpen,
        onAfterOpen,
        onRequestClose,
        overlayClassName,
        overlayRef,
        parentSelector,
        portalClassName,
        role,
        shouldCloseOnEsc,
        shouldCloseOnOverlayClick,
        shouldFocusAfterRender,
        shouldReturnFocusAfterClose,
        style
    } = props

    if (blindClassName) {
        console.warn(
            `Warning ModalBox: Using blindClassName property is
            deprecated, use overlayClassName instead.`
        )
    }

    return (
        <Modal
            className={cx(styles['content'], className, 't3sel-modal-box')}
            overlayClassName={cx(styles['overlay'], blindClassName, overlayClassName, 't3sel-modal-box-overlay')}
            {...{
                aria,
                ariaHideApp,
                bodyOpenClassName,
                closeTimeoutMS,
                contentLabel,
                contentRef,
                htmlOpenClassName,
                isOpen,
                onAfterOpen,
                onRequestClose,
                overlayRef,
                parentSelector,
                portalClassName,
                role,
                shouldCloseOnEsc,
                shouldCloseOnOverlayClick,
                shouldFocusAfterRender,
                shouldReturnFocusAfterClose,
                style
            }}
        >
            {children}
        </Modal>
    )
}

ModalBox.propTypes = propTypes
ModalBox.defaultProps = defaultProps

export default ModalBox
